import { useTranslation } from "react-i18next";

import Button from "~/components/buttons/Button/Button";
import type { Absence } from "~/modules/absence/api/absence/absenceTypes.ts";
import type { CalendarAbsenceActions } from "~/modules/dashboard/components/TimeTrackingCalendar/calendarDays.ts";
import { EntryCard } from "~/modules/dashboard/components/TimeTrackingCalendar/EntryCard.tsx";
import { getAbsenceColor, phoneButtonClass } from "~/modules/dashboard/components/TimeTrackingCalendar/entryStyles.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { formatDateRange } from "~/utils/dateAndTimeUtils.ts";

type Props = {
	absence: Absence;
	absenceName: string;
	absenceActions: CalendarAbsenceActions;
	onClose: () => void;
};

/** The open absence. `getRules` gives no reason, so a refused action has no button. */
export function AbsenceCard({ absence, absenceName, absenceActions, onClose }: Props) {
	const { t } = useTranslation();
	const { isDeletable, isEditable } = absenceActions.getRules(absence, absenceName);

	const closeThen = (action: () => void) => () => {
		onClose();
		action();
	};

	return (
		<EntryCard color={getAbsenceColor(absenceName)}
				   title={t(normalizeTKey("entities:absenceType." + absenceName))}
				   subtitle={formatDateRange(new Date(absence.startDate), new Date(absence.endDate))}
				   onClose={onClose}>
			<div className="flex gap-2 pt-1">
				{isEditable && (
					<Button theme="accent"
							size="sm"
							className={phoneButtonClass}
							onClick={closeThen(() => absenceActions.openEdit(absence.id))}>Bearbeiten</Button>
				)}
				{isDeletable && (
					<Button theme="danger"
							size="sm"
							className={phoneButtonClass}
							onClick={closeThen(() => absenceActions.openDelete(absence.id))}>Löschen</Button>
				)}
				<Button theme="white"
						size="sm"
						className={`border border-gray-300 ${phoneButtonClass}`}
						onClick={closeThen(() => absenceActions.download(absence, absenceName))}>.ics</Button>
			</div>
		</EntryCard>
	);
}
